/**
 * The **NPC** spawn adapter: class/template composition, per-tier stat normalization, the
 * optional-feature budget arithmetic, and the NPC stat-bonus pre-pass.
 */

import type { Compendium } from "@lancer-vtt/compendium";
import type { AffordanceCap, AffordanceLimited, AffordanceSource, CombatantBase, FrozenNpcFeature, FrozenSystem, NpcWear, Side, WeaponProfile, WeaponRange } from "@lancer-vtt/domain";
import { affordanceKey, BIOLOGICAL_STATUS, weaponInstanceKey } from "@lancer-vtt/domain";
import { ok, err } from "@lancer-vtt/shared";
import { applyBonus, type Bonus, type ModifierVars } from "../../bonus";
import { coreActionIdsFor } from "../coreActionCatalog";
import { gatherActions, type LoadedOverlay } from "../effects/loader";
import { buildAffordances, normalizeTechActivation, parseActionFrequency } from "./actionFreeze";
import { freezeDisplayFeatures, freezeNpcWeapon, freezeTagRefs, isNpcWeaponFeature, parseRange, tagVal, tierResolve, tierResolveNumber, type RawNpcFeature, type RawWeapon } from "../weaponProfile";
import type { SpawnResult } from "./types";
import { freezeStructureTable } from "./structureTableFreeze";
import { isTechAttackAction } from "../techAttackAction";

/**
 * The subset of an NPC class block the spawn adapter actually reads. **Normalize
 * only the fields a Combatant base needs, demand-driven**: v2 packs store stats as
 * all-tiered arrays, v3 mixes scalars + tiered arrays. We read the canonical *v3-shaped* fields and
 * tolerate both a scalar and a per-tier array for each stat we consume; we do
 * **not** speculatively normalize the whole NPC shape.
 *
 * Stats consumed (the manual-tier base block): `hp`, `heatcap`, `armor`,
 * `evasion`, `edef`, `speed`, `size`, `save`. Anything an NPC class block stores
 * beyond these (actions, features, active_effects, …) is ignored at spawn — extend this
 * normalization demand-driven when something actually needs a field.
 *
 * Loosely typed (`unknown`-ish) because NPC pack data arrives via the
 * compendium's `.passthrough()` and core/free data ships no NPC content — the
 * canonical shape is pinned by the synthetic test fixture, not by a Zod schema
 * here.
 */
export type NpcClassBlock = {
  id?: string;
  name?: string;
  /**
   * The class's RAW **role** tag (COMP/CON `NpcClass.ts`; our lenient `NpcClassSchema` passes
   * it through verbatim — `packages/compendium/src/schemas.ts`). `spawnNpc` reads this
   * case-insensitively for
   * `"biological"` to attach the {@link BIOLOGICAL_STATUS} tech-immunity status at spawn
   * (Core p.282's Biological NPC tag). **Not** the heat signal — `maxHeat` being absent (drones
   * included) is a separate, unrelated marker; do not conflate the two.
   */
  role?: string;
  /**
   * Per-stat values, each either a scalar (v3 may store some as scalars) or a
   * 3-element per-tier array (v2/v3 tiered stats). `tier` selects the array
   * element.
   */
  stats?: NpcStatBlock;
  /**
   * The class's **always-on feature ids**. **v2** NPC packs carry this explicitly on the class;
   * **v3** per-file packs omit it (the only base signal there is the feature's own `base: true`
   * flag), so {@link spawnNpc} falls back to deriving the base set from the compendium when this
   * is absent.
   */
  base_features?: string[];
  /**
   * The class's **optional feature id list** — the v2 legacy fallback for the GM-picked pool,
   * mirroring {@link base_features}'s dual pattern. v3 per-file packs omit this (the only signal
   * there is a `npc_feature` with a matching `origin` and NO `base:true` flag).
   */
  optional_features?: string[];
  /**
   * The class's own optional-feature-pick **budget override** (COMP/CON
   * `NpcClass.OptionalClassMin/Max`). Absent on almost every real class
   * (defaults apply: min 0, max 2); a handful of real v3 classes (`npcc_monstrosity`) set an
   * explicit override. Templates additionally *top up* this same class-list budget via their
   * own `optionalClassMin`/`optionalClassMax` (see {@link NpcTemplateBlock}).
   */
  optionalClassMin?: number;
  optionalClassMax?: number;
}

/**
 * The subset of an NPC **template** block the spawn adapter reads (COMP/CON
 * `NpcTemplate.ts`). Structurally the same shape as {@link
 * NpcClassBlock} for feature resolution (base/optional dual v2-array / v3-origin+flag pattern),
 * plus the template-specific optional-feature-budget fields COMP/CON's `NpcTemplateController`
 * (`FeatureRequirements`) reads verbatim — every budget field defaults to `0`/absent when the
 * template doesn't carry it (`data.optionalMin || 0`, etc. — COMP/CON's own default, not ours).
 */
export type NpcTemplateBlock = {
  id?: string;
  name?: string;
  /** v3 denormalized always-on feature ids for this template (mirrors {@link NpcClassBlock.base_features}). */
  base_features?: string[];
  /** v2 legacy explicit optional-feature id list (mirrors {@link NpcClassBlock.optional_features}). */
  optional_features?: string[];
  /**
   * This template's **own optional-feature-list budget** — how many of ITS OWN
   * optional features the GM picks. Both default to `0` (no picks) when absent; `optionalPerTier`
   * (below), when set, is added on top, multiplied by the deployment tier.
   */
  optionalMin?: number;
  optionalMax?: number;
  /** Multiplies by tier and adds to BOTH {@link optionalMin} and {@link optionalMax} (Veteran: `1` → tier-2 Veteran picks 2 of its own list). */
  optionalPerTier?: number;
  /** This template's **top-up to the class's own optional-feature budget** (Elite: min/max `1` → exactly 1 extra class pick). Defaults to `0`. */
  optionalClassMin?: number;
  optionalClassMax?: number;
  /** Multiplies by tier and adds to both class-budget top-ups above. Defaults to `0` (no real template sets this in shipped data). */
  optionalClassPerTier?: number;
  /**
   * Advisory-only incompatible template ids — an **affordance** the CLI/picker surface as a
   * warning, never a hard reject at this engine tier. This engine tier does not read or enforce
   * the field at all; it's carried on the type purely so a caller who resolves the raw template
   * block has it to hand to the advisory layer.
   */
  prohibitTemplates?: string[];
  /** The NPC tag this template forces (`npct_vehicle`: `"vehicle"`, `npct_ship`: `"ship"`), frozen as `base.npcTag`. */
  forceTag?: string;
}

/** The raw NPC stat block — each field a scalar or a per-tier `[t1, t2, t3]` array. */
export type NpcStatBlock = {
  hp?: number | number[];
  heatcap?: number | number[];
  armor?: number | number[];
  /**
   * Evasion. The synthetic test fixture stores it as `evasion`; the **real**
   * Massif NPC `.lcp` (both v2 and v3) names this stat `evade`. Both keys are
   * accepted at read time ({@link readNpcStat} with the `evade` alias) so the
   * adapter normalizes whichever the loaded pack carries.
   */
  evasion?: number | number[];
  evade?: number | number[];
  edef?: number | number[];
  speed?: number | number[];
  size?: number | number[];
  save?: number | number[];
  attack_bonus?: number | number[];
  /** The consequence tracks — real data reads fall back to `1` when absent. */
  structure?: number | number[];
  stress?: number | number[];
  /** The four raw HASE axes (COMP/CON's `INpcClassStats`). */
  hull?: number | number[];
  agility?: number | number[];
  systems?: number | number[];
  engineering?: number | number[];
  [key: string]: number | number[] | undefined;
}

/** NPC tiers are 1–3 in Lancer. */
export type NpcTier = 1 | 2 | 3;

/**
 * Read one NPC stat at a tier, normalizing scalar-or-tiered storage (the
 * demand-driven v2/v3 seam). A scalar is tier-invariant; a tiered array is
 * indexed by `tier - 1`. Falls back to `fallback` when absent or the tier index
 * is out of range.
 *
 * **A tier entry may itself be a choice list.** v3 stores `size` as one list of
 * permitted sizes per tier (Goliath `[[3],[3],[3]]`, Assassin `[[0.5,1],…]`,
 * Monstrosity `[[1,2],[1,2],[1,2,3]]`). The first choice wins, matching COMP/CON's
 * `NpcClassController` (`sizeVal[0]`). Before this read, every such class fell back
 * to `fallback`, so a Goliath spawned at Size 1.
 */
type ReadNpcStatArgs = {
  stats: NpcStatBlock | undefined;
  key: keyof NpcStatBlock;
  tier: NpcTier;
  fallback: number;
  /** Alternate key names to try (in order) when `key` is absent — e.g. `evade` for `evasion`. */
  aliases?: ReadonlyArray<keyof NpcStatBlock>;
};

function readNpcStat(args: ReadNpcStatArgs): number {
  const { stats, key, tier, fallback, aliases = [] } = args;
  for (const k of [key, ...aliases]) {
    const v = stats?.[k];
    if (typeof v === "number") return v;
    if (Array.isArray(v)) {
      const at: unknown = v[tier - 1];
      if (typeof at === "number") return at;
      if (Array.isArray(at) && typeof at[0] === "number") return at[0];
    }
  }
  return fallback;
}

/**
 * Resolve one origin's (a class OR a template) **base and optional** feature lists out of the
 * compendium. Mirrors COMP/CON's dual pattern (`NpcClass.ts`/`NpcTemplate.ts`
 * `BaseFeatures`/`OptionalFeatures` getters): a pack may state the edge on the **origin**
 * (explicit `base_features`/`optional_features` id arrays — the v2 layout) or on the
 * **feature** (`origin` + a `base: true` flag — the v3 layout). This resolves the **union** of
 * both, explicit first, deduped by feature id across the two buckets (a feature claimed as base
 * is never also offered as optional). A `base_features`/`optional_features` id that doesn't
 * resolve is **skipped**, never a crash.
 *
 * **The union is load-bearing, not belt-and-braces.** It used to be "prefer explicit, else
 * derive", justified by real packs only ever populating one mechanism — but as of
 * `npc-compendium-rendering` Phase 1 the v3 adapter (`v3ZipToRawPack` → `linkV3Features`)
 * synthesizes the origin-side arrays from the feature-side edges, so a v3 class now populates
 * BOTH. Under the old short-circuit that silently narrowed the pool to what the v3 pack itself
 * declared, dropping any feature a *later-merged* pack contributes by pointing its `origin` at
 * an existing class — the supplementary-pack case (and what `spawn.test.ts`'s two synthetic
 * bonus-pass fixtures exercise). The union restores it for both layouts at once.
 *
 * **For every SHIPPED pack the union is a no-op, measured, not assumed.** Comparing this
 * function against its pre-change form over all 45 classes+templates — the live world's pack
 * set (both base packs + the v2 pack + the v3 pack + session-one), and each NPC pack alone —
 * gives 0 differing pools, on the id SEQUENCE and not merely the set. The order half matters:
 * `composeNpcFeatures` dedups first-occurrence-wins and the bonus pre-pass applies overwrites
 * in order, so a reordered pool would be a real behaviour change. It holds because a v3 pack's
 * synthesized arrays are built by walking the same feature bucket, in the same order, that the
 * derived half re-filters out of `compendium.list`. So the only behaviour this widened is the
 * cross-pack case that was broken.
 *
 * Exported because the CLI's `npc-info` verb and `add --npc --feature` typo-check both need
 * the same base/optional pool a class or template offers — reusing this avoids a second,
 * potentially-drifting copy of the v2/v3 dual-pattern resolution in `cli/src/encounter-cli.ts`.
 */
export function resolveOriginFeatures(args: {
  originId: string | undefined;
  explicitBase: string[] | undefined;
  explicitOptional: string[] | undefined;
  compendium: Compendium;
}): { base: RawNpcFeature[]; optional: RawNpcFeature[] } {
  const { originId, explicitBase, explicitOptional, compendium } = args;
  const byIds = (ids: string[]): RawNpcFeature[] =>
    ids
      .map((id) => compendium.getById("npc_feature", id) as RawNpcFeature | undefined)
      .filter((f): f is RawNpcFeature => f !== undefined);

  // Shared across both buckets so an id can only land once, and base wins: the base line
  // below is fully evaluated before the optional line, so an explicitly-listed base feature
  // is never re-offered as an optional pick by the derived half.
  const seen = new Set<string>();
  const take = (features: RawNpcFeature[]): RawNpcFeature[] => {
    const out: RawNpcFeature[] = [];
    for (const f of features) {
      if (f.id !== undefined) {
        if (seen.has(f.id)) continue;
        seen.add(f.id);
      }
      out.push(f);
    }
    return out;
  };

  const derived =
    originId === undefined
      ? []
      : (compendium.list("npc_feature") as RawNpcFeature[]).filter((f) => f.origin === originId);

  const base = [...take(byIds(explicitBase ?? [])), ...take(derived.filter((f) => f.base === true))];
  // A `deprecated` feature (v3's Moving Target, Blip, Reflexive Blow, Relocation: "this ability is
  // now collected in X; this feature can be safely removed") is never offered as a pick
  // (`npc-gaps` 2.5). None is a base feature.
  const optional = [...take(byIds(explicitOptional ?? [])), ...take(derived.filter((f) => f.base !== true))].filter((f) => f.deprecated !== true);
  return { base, optional };
}

/**
 * The composed result of merging a class + its applied templates + the GM's chosen optional
 * picks — the single feature list the rest of `spawnNpc` freezes from, plus the resolved
 * template blocks (for the noCrits-clear check and the budget/prohibit advisory layer).
 */
export type NpcComposition = {
  /**
   * The merged, deduped feature list, in application order: class base → each template's base
   * (application/`templateIds` order) → chosen class optionals → chosen template optionals
   * (each bucket in `optionalFeatureIds` order — the CLI `--feature` flag order). Deduped by
   * feature id, **first occurrence wins** (our stability call — COMP/CON concats without dedup,
   * so there's no real behavior to cite either way).
   */
  features: RawNpcFeature[];
  /** The resolved template blocks, in `templateIds` order — only the ids that actually resolved (honest degrade). */
  templates: NpcTemplateBlock[];
  /** The subset of the caller's `templateIds` that resolved — what {@link Combatant.templateIds} freezes. */
  resolvedTemplateIds: string[];
};

export type ComposeNpcFeaturesArgs = {
  npcClass: NpcClassBlock;
  /** Applied template ids, in application order. */
  templateIds: string[];
  /** GM-chosen optional feature ids, in flag order. */
  optionalFeatureIds: string[];
  compendium: Compendium;
};

export function composeNpcFeatures(args: ComposeNpcFeaturesArgs): NpcComposition {
  const { npcClass, templateIds, optionalFeatureIds, compendium } = args;

  const seen = new Set<string>();
  const merged: RawNpcFeature[] = [];
  const addFeature = (f: RawNpcFeature): void => {
    if (f.id !== undefined) {
      if (seen.has(f.id)) return; // dedup by feature id, first occurrence wins
      seen.add(f.id);
    }
    merged.push(f);
  };
  /** Pick from `pool` in the caller's `optionalFeatureIds` order (the "flag order" rule). */
  const chosenInFlagOrder = (pool: RawNpcFeature[]): RawNpcFeature[] => {
    const byId = new Map(pool.filter((f) => f.id !== undefined).map((f) => [f.id as string, f]));
    return optionalFeatureIds.map((id) => byId.get(id)).filter((f): f is RawNpcFeature => f !== undefined);
  };

  const classResolved = resolveOriginFeatures({
    originId: npcClass.id,
    explicitBase: npcClass.base_features,
    explicitOptional: npcClass.optional_features,
    compendium,
  });

  const templates: NpcTemplateBlock[] = [];
  const resolvedTemplateIds: string[] = [];
  const templateResolved: { base: RawNpcFeature[]; optional: RawNpcFeature[] }[] = [];
  for (const tid of templateIds) {
    const t = compendium.getById("npc_template", tid) as NpcTemplateBlock | undefined;
    if (!t) continue; // unresolved template ref — honest degrade, like every other freeze
    templates.push(t);
    resolvedTemplateIds.push(tid);
    templateResolved.push(
      resolveOriginFeatures({ originId: t.id, explicitBase: t.base_features, explicitOptional: t.optional_features, compendium }),
    );
  }

  // 1. class base.
  classResolved.base.forEach(addFeature);
  // 2. each template's base, in application order.
  templateResolved.forEach((tr) => tr.base.forEach(addFeature));
  // 3. chosen class optionals, in flag order.
  chosenInFlagOrder(classResolved.optional).forEach(addFeature);
  // 4. chosen template optionals, per template (application order), each in flag order.
  templateResolved.forEach((tr) => chosenInFlagOrder(tr.optional).forEach(addFeature));

  return { features: merged, templates, resolvedTemplateIds };
}

/**
 * One optional-feature-pick budget: `sourceId` names what it's for, `min`/`max` bound how many
 * the GM may pick from that source's own optional list. **ADVISORY**, not enforced at this
 * engine tier; the CLI/picker are the affordance layer that reads this to warn.
 */
export type NpcOptionalBudget = {
  sourceId: string;
  min: number;
  max: number;
};

export type NpcOptionalBudgets = {
  /** The class's optional-list budget, accumulated with every applied template's class-top-up. */
  classBudget: NpcOptionalBudget;
  /** Each applied template's OWN optional-list budget, one per template in application order. */
  templateBudgets: NpcOptionalBudget[];
};

/**
 * Compute the optional-feature-pick budgets for a class + its applied templates at a tier —
 * COMP/CON's `NpcTemplateController.FeatureRequirements`, ported verbatim:
 * the class list defaults to `{min: 0, max: 2}` (`optionalClassMin ?? 0` / `optionalClassMax ?? 2`),
 * each applied template tops that SAME class budget up via its own `optionalClassMin`/`Max`
 * (± `optionalClassPerTier * tier`); each template's own list starts at its `optionalMin`/`Max`
 * (`0` default) plus `optionalPerTier * tier`. Pure/ADVISORY — no enforcement here.
 */
export function computeNpcOptionalBudgets(args: {
  npcClass: NpcClassBlock;
  templates: NpcTemplateBlock[];
  tier: NpcTier;
}): NpcOptionalBudgets {
  const { npcClass, templates, tier } = args;
  let classMin = npcClass.optionalClassMin ?? 0;
  let classMax = npcClass.optionalClassMax ?? 2;

  const templateBudgets: NpcOptionalBudget[] = templates.map((t) => {
    let min = t.optionalMin ?? 0;
    let max = t.optionalMax ?? 0;
    if (t.optionalPerTier) {
      min += t.optionalPerTier * tier;
      max += t.optionalPerTier * tier;
    }
    classMin += t.optionalClassMin ?? 0;
    classMax += t.optionalClassMax ?? 0;
    if (t.optionalClassPerTier) {
      classMin += t.optionalClassPerTier * tier;
      classMax += t.optionalClassPerTier * tier;
    }
    return { sourceId: t.id ?? "?", min, max };
  });

  return { classBudget: { sourceId: npcClass.id ?? "?", min: classMin, max: classMax }, templateBudgets };
}

/**
 * The raw tier-resolved NPC stat block the bonus pre-pass runs over — every stat a `bonuses`
 * entry can target, read the SAME way `spawnNpc`
 * already reads them (scalar-or-tiered, `evade`/`sensor` aliases folded in first so the bonus
 * map only ever needs the canonical name). `attackBonus` is deliberately excluded — no shipped
 * or COMP/CON-recognized bonus id targets a flat NPC attack bonus.
 */
export type ResolvedNpcStats = {
  hp: number;
  heatcap: number;
  armor: number;
  evasion: number;
  edef: number;
  speed: number;
  size: number;
  save: number;
  structure: number;
  stress: number;
  sensors: number;
  hull: number;
  agility: number;
  systems: number;
  engineering: number;
};

/** Read the raw {@link ResolvedNpcStats} off a class's stat block at a tier (pre-bonus). */
export function resolveNpcStats(npcClass: NpcClassBlock, tier: NpcTier): ResolvedNpcStats {
  const stats = npcClass.stats;
  const read = (key: keyof NpcStatBlock, fallback: number, aliases?: (keyof NpcStatBlock)[]) =>
    readNpcStat({ stats, key, tier, fallback, aliases });
  return {
    hp: read("hp", 0),
    heatcap: read("heatcap", 0),
    armor: read("armor", 0),
    evasion: read("evasion", 0, ["evade"]),
    edef: read("edef", 0),
    speed: read("speed", 0),
    size: read("size", 1),
    save: read("save", 0),
    structure: read("structure", 1),
    stress: read("stress", 1),
    sensors: read("sensor_range", 0, ["sensors", "sensor"]),
    hull: read("hull", 0),
    agility: read("agility", 0),
    systems: read("systems", 0),
    engineering: read("engineering", 0),
  };
}

/**
 * Every stat-modifying `bonuses[].id` this engine recognizes, mapped onto its
 * {@link ResolvedNpcStats} field (verified against COMP/CON's `bonus_dictionary.ts`).
 * `evade`/`sensor`/`sensors` alias onto the same canonical target as `readNpcStat`'s own alias
 * list — the bonus-id mapping must honor the same spellings the stat-read side does — even
 * though shipped data only ever uses the
 * canonical `evasion`/`sensor` spelling — defensive, not dead, for a homebrew/future pack.
 */
const BONUS_STAT_MAP: Readonly<Record<string, keyof ResolvedNpcStats>> = {
  hp: "hp",
  heatcap: "heatcap",
  armor: "armor",
  evasion: "evasion",
  evade: "evasion",
  edef: "edef",
  speed: "speed",
  size: "size",
  save: "save",
  structure: "structure",
  stress: "stress",
  sensor: "sensors",
  sensors: "sensors",
  sensor_range: "sensors",
  hull: "hull",
  agility: "agility",
  systems: "systems",
  engineering: "engineering",
};

/** The stats a bonus can raise to 20 at most (Evasive, Legion). */
const BONUS_CAPPED_AT_20 = ["evasion", "edef"] as const satisfies readonly (keyof ResolvedNpcStats)[];

export type NpcBonusPassResult = {
  /** The bonus-adjusted stats — feed these into `CombatantBase` construction, never the raw ones. */
  stats: ResolvedNpcStats;
  /**
   * Unmapped, non-null bonus ids encountered (deduped, first-seen order) — the "visible skips"
   * (just `deploy_count`/`sizes` in shipped data; `activations`/`activations_pct` are honored
   * below). The CLI prints a one-line note off this when non-empty.
   */
  skippedBonusIds: string[];
  /**
   * The ELITE `activations: +1` bonus, tier-resolved and stacked (overwrite/replace/
   * additive, same precedence as every {@link ResolvedNpcStats} field) — `undefined` when no
   * merged feature carries an `activations` bonus. Feeds `CombatantBase.extraActivations`
   * verbatim; never defaults to `0` here, so a spawn without the bonus omits the frozen field
   * entirely, matching every other optional freeze in {@link spawnNpc}.
   */
  extraActivations?: number;
  /**
   * The ULTRA `activations_pct` threshold, tier-resolved and stacked identically —
   * `undefined` when no merged feature carries an `activations_pct` bonus. Feeds
   * `CombatantBase.activationsPctThreshold` verbatim — this is the raw threshold `val`,
   * NOT a resolved player count or bonus.
   */
  activationsPctThreshold?: number;
};

/** A zeroed {@link ModifierVars} context — NPC bonuses never reference a PC-only var (`{grit}` etc.). */
const NPC_BONUS_VARS: ModifierVars = { grit: 0, ll: 0, hull: 0, agi: 0, sys: 0, eng: 0 };

/**
 * The NPC bonus pre-pass: walk every merged feature's TOP-LEVEL `bonuses` array (never
 * `active_effects[].bonuses` — those are conditional/triggered, out of scope), tier-resolve each
 * `val` (a per-tier array collapses to this tier's scalar BEFORE `applyBonus` sees it — its own
 * array handling sums formula terms, a different semantic), **drop a null/absent `val` first** (a
 * `{id, accuracy}`-only entry is prose-only and must never false-flag a recognized stat like
 * `systems` as skipped), THEN either apply it (mapped id) or record it (unmapped id → `skippedBonusIds`). Finally runs
 * the shared `applyBonus` per stat over the full collected bonus list — same
 * overwrite-beats-replace-beats-additive precedence a mech gets.
 */
export function applyNpcStatBonuses(args: {
  stats: ResolvedNpcStats;
  features: RawNpcFeature[];
  tier: NpcTier;
}): NpcBonusPassResult {
  const { stats, features, tier } = args;
  const bonuses: Bonus[] = [];
  const skipped: string[] = [];
  const skippedSeen = new Set<string>();
  // `activations`/`activations_pct` don't map onto a `ResolvedNpcStats` field (there's no
  // "base" stat to bonus onto — `extraActivations`/
  // `activationsPctThreshold` are frozen raw identity, not a resolved stat), so they're
  // collected into their own bonus lists rather than `BONUS_STAT_MAP`, but tier-resolved and
  // stacked (overwrite/replace/additive) via the SAME `applyBonus` call every other stat gets.
  const activationBonuses: Bonus[] = [];
  const activationsPctBonuses: Bonus[] = [];

  for (const f of features) {
    const raw = f.bonuses;
    if (!Array.isArray(raw)) continue;
    for (const b of raw) {
      const id = b?.id;
      if (typeof id !== "string") continue;
      const val = tierResolve((b as { val?: unknown }).val, tier);
      if (val === undefined || val === null) continue; // prose-only (accuracy-only) bonus — drop BEFORE the skip-list check
      // v3 spells one overwrite `override` (the Ship's Massive Size, `{ id: "size", val: 4, override:
      // true }`; RAW: "The Ship is Size 4"), so it sets the stat rather than adding to it.
      const overwrite = b.overwrite === true || (b as { override?: unknown }).override === true ? true : undefined;
      if (id === "activations") {
        activationBonuses.push({ id, val: val as Bonus["val"], overwrite, replace: b.replace });
        continue;
      }
      if (id === "activations_pct") {
        activationsPctBonuses.push({ id, val: val as Bonus["val"], overwrite, replace: b.replace });
        continue;
      }
      const mapped = BONUS_STAT_MAP[id];
      if (!mapped) {
        if (!skippedSeen.has(id)) {
          skippedSeen.add(id);
          skipped.push(id);
        }
        continue;
      }
      bonuses.push({ id: mapped, val: val as Bonus["val"], overwrite, replace: b.replace });
    }
  }

  const resolved = { ...stats };
  for (const key of Object.keys(stats) as (keyof ResolvedNpcStats)[]) {
    resolved[key] = applyBonus({ id: key, base: stats[key], bonuses, vars: NPC_BONUS_VARS });
  }
  // The two v3 bonuses to these stats print a ceiling: Evasive's "+4 EVASION (to a maximum of 20)"
  // and Legion's "+4 E-DEFENSE (to a maximum of 20)". A bonus raises the stat to 20 at most; a class
  // already at 20 (a tier-3 Hornet) keeps it.
  for (const key of BONUS_CAPPED_AT_20) {
    if (resolved[key] > stats[key]) resolved[key] = Math.max(stats[key], Math.min(resolved[key], 20));
  }
  return {
    stats: resolved,
    skippedBonusIds: skipped,
    ...(activationBonuses.length > 0
      ? { extraActivations: applyBonus({ id: "activations", base: 0, bonuses: activationBonuses, vars: NPC_BONUS_VARS }) }
      : {}),
    ...(activationsPctBonuses.length > 0
      ? {
          activationsPctThreshold: applyBonus({
            id: "activations_pct",
            base: 0,
            bonuses: activationsPctBonuses,
            vars: NPC_BONUS_VARS,
          }),
        }
      : {}),
  };
}

/** Template ids whose presence clears `noCrits` (RAW p.281's Veteran/Ultra exception; a hardcoded identity check, not data-driven). */
const NOCRIT_CLEAR_TEMPLATES = new Set(["npct_veteran", "npct_ultra"]);

/**
 * Spawn a combatant from an NPC class block at a given tier. Reads the base-block
 * stats (demand-driven; see {@link NpcClassBlock}), normalizing scalar-or-tiered
 * storage, and **freezes the class's `base_features`**: Weapon
 * features → the resolvable {@link CombatantBase.weapons} (so they display *and*
 * resolve through the existing attack pipeline as flat tier-damage), every other
 * feature → the display-only {@link CombatantBase.npcFeatures} stat block. The caller
 * mints the combatant `id` and supplies the `compendium` (the feature source — like
 * {@link spawnMech}).
 *
 * **Heat/stress/HASE/no-crit streamlining:** `maxHeat` freezes from the class's `heatcap` stat,
 * OMITTED (presence-based, no `null` sentinel) when the block lacks it or it resolves to `0` —
 * the Biological signature (RAW p.281: "no Heat Cap") is exactly this frozen absence, not a separate
 * tag; the heat cascade (`resolveHeatCascade`, `@lancer-vtt/rules`
 * `tables.ts`) reads it and converts incoming heat to Energy damage for such a carrier. `maxStress`
 * freezes from `stress` (fallback `1` — most NPCs have 1 STRESS); the cascade's `maxStress === 1`
 * branch grants Exposed instead of an Overheating-table roll. The four raw **HASE** axes
 * (`hull`/`agility`/`systems`/`engineering`) freeze from the class's own stats (fallback `0`),
 * feeding the shipped Grapple-break/Search contests a real number instead of the `0` proxy.
 * `base.noCrits: true` is set unconditionally (RAW: "No critical hits — except Veterans and
 * Ultras"); a Veteran/Ultra template clears it.
 *
 * **Biological spawn-attach:** a
 * class whose {@link NpcClassBlock.role} reads `"biological"` (case-insensitive) spawns with
 * the {@link BIOLOGICAL_STATUS} status pre-attached (`until_removed`) — the tech-legality gate
 * then folds its overlay entry's `{kind:"tech", except:["act_scan","act_lock_on"]}` immunity
 * scope exactly like any other status. This is the data marker (`role`), never `maxHeat`'s
 * absence — the heat-cascade signal above shares that absence with plain drones, which are NOT
 * tech-immune. The SAME `role` classifier also cashes out `base.destructionOutcome: "corpse"`
 * (vs. `"wreck"` otherwise), so `wreckConversion.ts` never reads the spawn-attached status at
 * rules time; the status keeps only its immunity/display/content-targeting jobs.
 *
 * Returns a rejection if the block carries no usable stats (a malformed/empty
 * class) so a driver surfaces it cleanly rather than deploying a 0-HP shell.
 *
 * Takes an **args object** (like {@link spawnMech}) rather than positional params —
 * the `compendium` brought the count past the 3-param lint cap (`CODE_STYLE.md`).
 */
export type SpawnNpcArgs = {
  combatantId: string;
  npcClass: NpcClassBlock;
  tier: NpcTier;
  compendium: Compendium;
  /**
   * Applied template ids, in application order — the caller (CLI/picker) has already resolved
   * refs to real ids; an id that doesn't resolve in THIS compendium honest-degrades (contributes
   * nothing, omitted from the frozen `Combatant.templateIds`), like every other freeze.
   * Omit/`[]` ⇒ no templates.
   */
  templateIds?: string[];
  /**
   * GM-chosen optional feature ids, in flag order — resolved ids, same honest-degrade
   * discipline as {@link templateIds}. Omit/`[]` ⇒ no optional picks.
   */
  optionalFeatureIds?: string[];
  /**
   * The owner of record the NPC is **born** with (`unit-control-assignment` Phase E). Omitted ⇒
   * `null`, GM-owned — every caller that predates Phase E. A player slot here is the same state a
   * GM-owned deploy plus a `set_controller` re-home reaches; this just saves the second gesture.
   */
  controllerSlotId?: string | null;
  /**
   * The side the NPC is born on, frozen onto {@link CombatantBase.allegiance}. Omitted ⇒ `"npc"`.
   * `spawnObject`'s `allegiance` arg is the precedent. Pass `"pc"` for an ally that activates in
   * the player bucket; a player owner on the `"npc"` side still activates in the hostile bucket,
   * which is legal and which the deploy surfaces warn about.
   */
  allegiance?: Side;
  /**
   * Deploy as a **keyed allied NPC** (`decisions/0196`): stamp `persistentNpcKey` so the END
   * COMBAT commit writes its wear back, and seed HP / Structure / Stress from `wear` (the
   * caller's read of `RosterState.npcWear[key]`; absent ⇒ pristine), each clamped to its max. A
   * `wear` marked `destroyed` refuses the spawn, as a destroyed printed mech does.
   */
  persistent?: { key: string; wear?: NpcWear };
  /**
   * The loaded action overlay, **optional** — it decides WHICH of a Limited feature's actions spend
   * a charge. A feature whose overlay entry authors `limitedUses` on any of its action clauses caps
   * only those clauses (the Aegis's Generate Blackwall spends its Limited 1; Dismiss Blackwall ends
   * the wall and spends nothing). Absent, or an entry authoring no `limitedUses`, ⇒ the feature's
   * `tg_limited` caps every action it mints. Mirrors `SpawnMechArgs.overlay`.
   */
  overlay?: LoadedOverlay;
};

/**
 * The frozen reach of one NPC feature action. The action's own RAW `range` wins (Sniper's Mark:
 * `[{type:"Range", val:25}]`). A tech action (`"Quick Tech"`/`"Full Tech"`) with no `range` reaches
 * the NPC's Sensors: v3 writes "within Sensors" only in the prose (Crush Targeting, the Witch's
 * tech), and RAW's default reach for a tech action is Sensors (Core p.64). The Sensors number is
 * frozen here from the post-bonus `base.sensors`, since a spawned NPC's Sensors never changes.
 * Anything else is left without a reach, which the activate walls read as ungated.
 */
function npcActionReach(action: unknown, sensors: number | undefined): WeaponRange[] {
  const entry = action as { range?: unknown; activation?: unknown } | undefined;
  const range = parseRange(entry?.range as RawWeapon["range"]);
  if (range.length > 0) return range;
  const isTech = entry?.activation === "Quick Tech" || entry?.activation === "Full Tech";
  return isTech && sensors !== undefined ? [{ kind: "range", val: sensors }] : [];
}

export function spawnNpc(args: SpawnNpcArgs): SpawnResult {
  const {
    combatantId,
    npcClass,
    tier,
    compendium,
    templateIds = [],
    optionalFeatureIds = [],
    controllerSlotId = null,
    allegiance = "npc",
    persistent,
    overlay,
  } = args;
  if (persistent?.wear?.destroyed) {
    return err(`spawnNpc: "${persistent.key}" is destroyed; can't deploy — clear its wear first (\`lvtt roster npc-wear clear ${persistent.key}\`)`);
  }
  const rawStats = npcClass.stats;
  if (!rawStats || typeof rawStats !== "object") {
    return err(`spawnNpc: NPC class "${npcClass.name ?? npcClass.id ?? "?"}" has no stat block`);
  }

  // Resolve class base + every applied template's base + the GM's chosen optionals (class-origin,
  // then each template's own list) into ONE merged, deduped feature list, THEN run the bonus
  // pre-pass over the raw tier-resolved stats BEFORE the base construction below reads them — so
  // `maxHeat`'s presence/absence derivation, the sensors omission, etc. all see the POST-bonus
  // numbers.
  const composition = composeNpcFeatures({ npcClass, templateIds, optionalFeatureIds, compendium });
  const rawResolvedStats = resolveNpcStats(npcClass, tier);
  const {
    stats: bonused,
    extraActivations,
    activationsPctThreshold,
  } = applyNpcStatBonuses({ stats: rawResolvedStats, features: composition.features, tier });

  if (bonused.hp <= 0) {
    return err(
      `spawnNpc: NPC class "${npcClass.name ?? npcClass.id ?? "?"}" resolved to 0 HP at tier ${tier}`,
    );
  }

  const base: CombatantBase = {
    maxHp: bonused.hp,
    // Heat cap (presence-based): absent-or-zero (POST-bonus) OMITS `maxHeat` entirely — the
    // frozen absence `resolveHeatCascade` reads to route incoming heat to Energy damage instead
    // of the (nonexistent) heat/stress track. A real positive value (base OR bonus-derived)
    // freezes verbatim — no heatcap bonus exists in shipped data today, but the
    // omission-derivation deliberately runs after the bonus pass so one would work. Set just
    // below, mirroring the other optional-freeze fields in this function.
    armor: bonused.armor,
    evasion: bonused.evasion,
    edef: bonused.edef,
    speed: bonused.speed,
    size: bonused.size,
    // The forced-save DC this NPC imposes when it makes a combatant save — the class's own
    // per-tier **Save Target** stat, read straight off the (bonus-folded) stat block, not a
    // formula. The `10 + tier` formula that used to sit here could only ever emit 10/11/12,
    // while the shipped NPC tier blocks span **8 to 18** — 44 of 90 printed values lie
    // outside the formula's range and one (Bastion T1, 8) lies below it. `decisions/0122`:
    // the data outranks a formula. Same value as `save` above by construction today (both
    // read `bonused.save`). The old sibling `save` field is gone: an NPC's save roll bonus
    // is its signed per-tier HASE axis (`hull`/`agility`/`systems`/`engineering`, frozen
    // below), resolved by `saveBonusFor`. Frozen at spawn; the resolver reads the
    // *attacker's* `saveTarget` as the default DC. See `decisions/0163`.
    saveTarget: bonused.save,
    // Consequence tracks: most NPCs have 1 STRUCTURE / 1 STRESS; read demand-driven off the
    // class's own stats (real Massif data never carries either, so the fallback does all the
    // work for every shipped class today). The `maxStress === 1` cascade branch (Exposed, no
    // Overheating table) is keyed on this.
    maxStructure: bonused.structure,
    maxStress: bonused.stress,
    // NPC weapon-attack bonus is a flat per-tier stat in Lancer; read it demand-driven
    // (scalar-or-tiered) like the rest, defaulting to 0 when the block doesn't carry it. The
    // resolver adds it to the d20. (No recognized bonus id targets this — see
    // `ResolvedNpcStats`' doc — so it's read directly, unbonused.)
    attackBonus: readNpcStat({ stats: rawStats, key: "attack_bonus", tier, fallback: 0 }),
    // The four raw HASE axes (COMP/CON's `INpcClassStats`), frozen exactly like a mech's
    // `pilotData.mechSkills.*` freeze in `spawnMech`: real values,
    // not a `0` proxy, feeding the shipped Grapple-break (`hull`) and Search (`systems`/`agility`)
    // contests. `engineering` has no engine consumer yet (see `CombatantBase.engineering`).
    hull: bonused.hull,
    agility: bonused.agility,
    systems: bonused.systems,
    engineering: bonused.engineering,
    // No critical hits (RAW p.281 "except Veterans and Ultras"): unconditional UNLESS an applied
    // template is identity-matched as the Veteran/Ultra exception (hardcoded, not data-driven;
    // no `bonuses` encoding exists anywhere in the data for this rule).
    ...(composition.resolvedTemplateIds.some((id) => NOCRIT_CLEAR_TEMPLATES.has(id)) ? {} : { noCrits: true }),
    // NPC-side unless the deployer chose otherwise (Phase E) — see `SpawnNpcArgs.allegiance`.
    allegiance,
  };

  // Heat cap, continued from the `base` literal's comment above: only frozen when it resolves
  // positive (POST-bonus) — otherwise `maxHeat` stays omitted (the Biological/no-heat-track
  // signal `hasHeatTrack`/`resolveHeatCascade` read).
  if (bonused.heatcap > 0) base.maxHeat = bonused.heatcap;

  // The NPC's **Sensors range** (tech-attack reach), POST-bonus. Only
  // frozen when it resolves positive — an NPC with no Sensors stat (and no bonus pushing it above
  // 0) omits the field and is left ungated for tech (range unknown, not zero), matching the
  // optional `CombatantBase.sensors` contract.
  if (bonused.sensors > 0) base.sensors = bonused.sensors;

  // Freeze the raw ELITE/ULTRA activation bonus components — NOT a resolved turn count
  // (`maxActivations`, `@lancer-vtt/domain` `activation.ts`, derives that live). Omitted entirely
  // when the merged features carry neither bonus (every mech and most NPCs), matching every other
  // optional freeze here. Deliberately no `playerCount`/party-size argument threaded in: a
  // resolved count frozen at spawn would be spawn-order-dependent, and the
  // GM-builds-hostiles-first order is the common case, not an edge case.
  if (extraActivations !== undefined) base.extraActivations = extraActivations;
  if (activationsPctThreshold !== undefined) base.activationsPctThreshold = activationsPctThreshold;

  // Freeze the merged feature list: Weapon features → resolvable `base.weapons` (flat
  // tier-damage at the weapon's own to-hit), everything else → the display-only
  // `base.npcFeatures` stat block. Single source per feature.
  //
  // Instance ids: NPC weapon features aren't mount/slot-indexed like a mech loadout, so each gets
  // `npc:${index}` — its position in the composed *merged* feature list (not just the class's
  // base subset), stable across a spawn.
  const weapons: WeaponProfile[] = [];
  const displayFeatures: RawNpcFeature[] = [];
  composition.features.forEach((f, i) => {
    if (isNpcWeaponFeature(f)) weapons.push(freezeNpcWeapon(f, tier, `npc:${i}`));
    else displayFeatures.push(f);
  });
  if (weapons.length > 0) base.weapons = weapons;
  const npcFeatures: FrozenNpcFeature[] = freezeDisplayFeatures(displayFeatures, tier);

  // Freeze every `type === "System"` feature into `base.equippedSystems` too — the SAME shape
  // `freezeSystems` (above) produces for a mech's loadout systems — so "destroy a system of your
  // choice" (System Trauma 4-6, Core p.282: only System-tagged features are destroyable,
  // Traits/Reactions/Tech never are) has a real NPC target set. `isNpcWeaponFeature`-style
  // discriminator: `f.type === "System"` (case-sensitive, matching the data's own capitalization
  // and `freezeDisplayFeatures`' own `type` passthrough).
  //
  // **`id = featureId` is load-bearing** (NOT a synthetic id): it's the exact SAME `f.id` that
  // prefixes this feature's granted-action composite ids (`${f.id}:${actionIndex}`, frozen into
  // `base.affordances` just below) — so destroying this frozen system correlates through
  // `destroyedSystemItemIds` (`actionRegistry.ts`) to drop its granted actions for free, with zero
  // new registry code. A feature with no `id` (never happens for a real compendium feature, but
  // honest-degrade like `freezeSystems`' unresolved-id skip) is skipped — nothing to correlate or
  // twin-mark.
  //
  // These System features STAY in `npcFeatures` for display too (single source, dual freeze) — the
  // twin-mark below stamps the SAME `system:${j}` instance id onto the display copy's new
  // `FrozenNpcFeature.instanceId` so the inspector can check `isItemDestroyed(resources,
  // instanceId)` directly on the display row without re-deriving which system it corresponds to.
  //
  // **Namespace**: `system:${j}` where `j` is the 0-based position among ONLY the System-typed
  // features (mirrors `freezeSystems`' `system:${i}` over a mech's `loadout.systems[]`) — disjoint
  // from weapons' `npc:${i}` scheme, so `destroyed:npc:0` and `destroyed:system:0` never collide.
  //
  // **Tags** freeze via the SAME `freezeTagRefs` helper as the display copy and as
  // `freezeSystems` — `tables.ts`'s `isSystemDestroyable` reads them to exclude an Indestructible
  // NPC system from the "of your choice" target set, exactly as it does for a mech system.
  //
  // **Conditional, like `spawnMech`'s own `equippedSystems`** (NOT unconditional like
  // `base.mounts`): omitted entirely when no System feature is present. `base.mounts` already
  // carries the "this combatant's loadout structure IS modeled" signal for the structure-damage
  // gate (set unconditionally, even to `[]`) — `equippedSystems` doesn't need to double as that
  // signal too, so it follows the ordinary optional-freeze convention every other field here
  // uses (and matches the mech path exactly).
  //
  // **No stat rollback:** a System's `bonuses[]` — if it has any — is already baked into
  // `bonused.*` above via `applyNpcStatBonuses`, at SPAWN time.
  // Destroying the frozen system later can never retroactively un-apply a number already folded
  // into this combatant's frozen stat block (`maxHp`/`armor`/etc.) — this is the frozen-base
  // model working as designed, not a bug. And a pure-display System (no `actions[]`, no
  // `bonuses[]`) has NO mechanical footprint at all when destroyed — just the DESTROYED display
  // tag on an inert stat-block entry; don't imply teeth that aren't there.
  const equippedSystems: FrozenSystem[] = [];
  let systemIdx = 0;
  displayFeatures.forEach((f, i) => {
    if (f.type !== "System" || f.id === undefined) return;
    const instanceId = `system:${systemIdx++}`;
    // `tier` for the same reason the display + weapon freezes take one: an NPC tag val can be
    // the printed tier notation (`"{2/3/4}"`), resolvable only against the spawning tier.
    const tags = freezeTagRefs(f.tags, tier);
    equippedSystems.push({ id: f.id, instanceId, name: npcFeatures[i]!.name, ...(tags.length > 0 ? { tags } : {}) });
    npcFeatures[i] = { ...npcFeatures[i]!, instanceId };
  });
  if (npcFeatures.length > 0) base.npcFeatures = npcFeatures;
  if (equippedSystems.length > 0) base.equippedSystems = equippedSystems;

  // Freeze every remaining (non-Weapon, non-System) merged feature's id into
  // `base.npcFeatureEffectIds` (Gap A) — Trait/Reaction/Tech features, which otherwise have NO
  // gather vector at all: `gatherHandlersAt` walks exactly `STATUS_VECTOR`,
  // `PILOT_EFFECT_VECTOR`, `SYSTEM_VECTOR`, and only `equippedSystems` (above) fed the last of
  // those for an NPC feature. `docs/agents/memories/pack-authoring.md` "NPC overlay entries:
  // the carrier type gate" documents the closed gap. `displayFeatures` already excludes Weapon
  // features (the freeze above); this ALSO excludes System features (`f.type === "System"`,
  // the SAME case-sensitive check the `equippedSystems` freeze just above uses) so a System
  // feature's id never lands here too — riding both `equippedSystems`/`SYSTEM_VECTOR` AND
  // `npcFeatureEffectIds`/`PILOT_EFFECT_VECTOR` would double-gather it (the Markerlight
  // 4-heat-instead-of-2 hazard; an entry test pins this exclusion — see `npc.test.ts`).
  //
  // Rides the SAME merged flat list `combatantEffectIds` composes from `pilotEffectIds` /
  // `frameEffectIds` / `pilotGearEffectIds` — no new vector object, exactly the shape that
  // closed the pilot-gear gap on 2026-07-25 (`freezePilotGearEffectIds`). A feature with no
  // `id` is skipped, matching every other freeze here. Conditional, the ordinary
  // optional-freeze convention every other field here uses: omitted when the merged feature
  // list carries no Trait/Reaction/Tech feature.
  const npcFeatureEffectIds = displayFeatures
    .filter((f) => f.type !== "System" && f.id !== undefined)
    .map((f) => f.id!);
  if (npcFeatureEffectIds.length > 0) base.npcFeatureEffectIds = npcFeatureEffectIds;
  // A feature that names a pack structure table (Unique Physiology → Monstrosity Structure Damage).
  const structureTable = freezeStructureTable({ featureIds: npcFeatureEffectIds, compendium });
  if (structureTable !== undefined) base.structureTable = structureTable;

  // Freeze a SYNTHETIC singleton `FrozenMount` per NPC weapon — NPCs have no real mount concept
  // (unlike a mech's slot-resolved loadout), so RAW's "all weapons on one mount of your choice"
  // collapses to "one weapon of your choice" per weapon. Each mount's sole slot carries the
  // SAME `weaponInstanceId` (`npc:${i}`) `freezeNpcWeapon` already minted above — no second
  // identity namespace, just a cross-reference into `base.weapons` (mirrors `freezeMounts`'s own
  // `weaponInstanceId` cross-reference for mechs). `type: "npc"` is a literal marker (NPCs have no
  // `MountData` to read a real mount-type string from).
  //
  // Deliberately assigned UNCONDITIONALLY (even `[]` for a weaponless NPC) — this diverges from
  // `spawnMech`'s `if (mounts.length > 0)` guard on purpose. For a mech, an empty/absent loadout
  // is a genuinely unbuilt state (the mount array's absence there means "not modeled yet," so
  // `resolveStructureDamage`'s gate keeping that combatant on the loud-warning path is correct).
  // For an NPC, the mount set is ALWAYS fully and honestly derivable from its frozen weapon list
  // — there is no "not yet built" state distinct from "has zero weapons." So `base.mounts` being
  // PRESENT (even empty) is what tells that gate "this combatant's loadout structure IS modeled":
  // a weaponed NPC lights up the real 1-3 destroy target set, and a genuinely gearless NPC (no
  // weapons, no systems) correctly falls through to the RAW "no valid mount/system" → Direct Hit
  // escalation instead of the warning dead-end, rather than silently stopping at "model gap." The
  // warning branch stays in place for every OTHER combatant kind that can still spawn with no
  // frozen loadout structure at all (e.g. a mech with a literally empty loadout).
  base.mounts = weapons.map((w) => ({ type: "npc", slots: [{ weaponInstanceId: weaponInstanceKey(w) }] }));

  // Freeze one **affordance record** per merged feature action — the NPC mirror of `spawnMech`'s
  // `freezeActionIds` + `buildAffordances`, over the merged NPC feature list instead of a
  // loadout. An NPC feature is the granted class exactly as a mech system is: the feature id is
  // the item, the position in its `actions[]` is the index. `enumerateActions`' granted branch is
  // kind-agnostic, and `lookupGranted` also tries `npc_feature`, so these fold into the
  // inspector's action rows with no further work.
  const grantedSources: AffordanceSource[] = [];
  // Recharge gate (`npc-features-fielded-roster` Phase 3.1, RAW Core p.281): every granted action
  // minted off a feature carrying `tg_recharge {N}` carries its target N on its own record — a
  // Trait's `tg_recharge` tag (Charge, Avalanche Charge) reads exactly like a System's (Impale,
  // Earthshaker Shells); this freeze doesn't care which. **Invariant**: no recharge-tagged
  // granted action ships without its record carrying the target — `checkGrantedActionLegality`
  // and `resolveRecharge` both read it, never the compendium. The join below is spawn-LOCAL;
  // `base.actionRecharge` itself was deleted in `affordance-table` Phase 3.1.
  const recharge: Record<string, number> = {};
  // The reach of each minted action (`CombatantBase.actionRanges`, `npc-features-fielded-roster`
  // Phase 16.1) — sparse, the same map `freezeActionIds` builds for a mech. `checkActivateSiteRange`
  // and `checkActivateUnitReach` read it.
  const actionRanges: Record<string, WeaponRange[]> = {};
  // The frozen tech-attack fact (`CombatantBase.techAttackActions`) for every feature action with a
  // top-level `attack: "tech"` (Tear Down, Predatory Logic, Petrify) — the NPC mirror of
  // `freezeActionIds`' map, and what admits the action id past `legalTechAttackWeaponIds` on
  // `request_attack`. The feature's own printed bonus/accuracy (Tear Down "+2/+4/+6, +1
  // Accuracy") ride the entry so the roll uses them rather than the NPC's Systems.
  const techAttackActions: NonNullable<CombatantBase["techAttackActions"]> = {};
  const featureNames: Record<string, string> = {};
  // Two facts per action, frozen independently (`decisions/0202`). Limited pool
  // (`npc-limited-uncapped`): a feature's `tg_limited {N}` freezes the mech's item-level pool
  // (`freezeActionCaps`' item arm) on every action it mints, pooled on the feature id so its actions
  // drain one charge count — unless its overlay entry authors `limitedUses` on some clause, which
  // then names the pooled actions and their max (see `SpawnNpcArgs.overlay`). Rate
  // (`npc-frequency-caps`): an action's RAW `frequency` ("1/round", "1/turn", "1/scene") freezes
  // unpooled, per action, as in `freezeActionCaps`. A Reaction's is skipped: it fires through the
  // reaction windows and spends a named reaction (`canUseReaction`, 1/round per name), so a frozen
  // rate would be one nothing spends. Bombing Bay's Drop Bomb carries both: `1/round` and Limited 3.
  const caps: Record<string, AffordanceCap> = {};
  const limitedPools: Record<string, AffordanceLimited> = {};
  for (const f of composition.features) {
    if (f.id === undefined) continue;
    const actions = Array.isArray(f.actions) ? f.actions : [];
    // A deploy affordance per `deployables[]` entry, offset past `actions[]` exactly as
    // `freezeActionIds` mints a mech system's (`<id>:<actions.length + j>`), so a deploy-only
    // System (Razor Swarms) carries its Recharge and Limited on a frozen record the overlay's
    // `deploy_object` clause resolves through. Unauthored, the row reads "GM adjudicates".
    const deployCount = Array.isArray(f.deployables) ? f.deployables.length : 0;
    if (typeof f.name === "string" && f.name !== "" && actions.length + deployCount > 0) featureNames[f.id] = f.name;
    const rechargeTarget = tagVal(
      f.tags?.find((t) => t.id === "tg_recharge"),
      tier,
    );
    const limited = tagVal(
      f.tags?.find((t) => t.id === "tg_limited"),
      tier,
    );
    const authored = overlay ? gatherActions({ ownerId: combatantId, entityId: f.id, overlay }).map((c) => c.effect.limitedUses?.max) : [];
    const limitedAt = (i: number) => (authored.some((m) => m !== undefined) ? (authored[i] ?? 0) : limited);
    for (let i = 0; i < actions.length + deployCount; i++) {
      const source: AffordanceSource = { kind: "granted", itemId: f.id, index: i };
      grantedSources.push(source);
      if (rechargeTarget > 0) recharge[affordanceKey(source)] = rechargeTarget;
      const max = limitedAt(i);
      if (max > 0) limitedPools[affordanceKey(source)] = { max, pool: f.id };
      if (i >= actions.length) continue;
      const action = actions[i] as { activation?: unknown; frequency?: string } | undefined;
      const rate = action?.activation === "Reaction" ? null : parseActionFrequency(action?.frequency);
      if (rate) caps[affordanceKey(source)] = rate;
      const reach = npcActionReach(actions[i], base.sensors);
      if (reach.length > 0) actionRanges[affordanceKey(source)] = reach;
      const entry = actions[i] as { attack?: unknown; activation?: unknown } | undefined;
      // `attack: "tech"` on the action, or nested in the effect a hit applies (`isTechAttackAction`).
      const activation = isTechAttackAction(entry) ? normalizeTechActivation(entry?.activation) : null;
      if (activation) {
        const attackBonus = tierResolveNumber(f.attack_bonus, tier);
        const accuracy = tierResolveNumber(f.accuracy, tier);
        techAttackActions[affordanceKey(source)] = {
          activation,
          ...(attackBonus !== undefined ? { attackBonus } : {}),
          ...(accuracy !== undefined ? { accuracy } : {}),
        };
      }
    }
  }
  if (Object.keys(actionRanges).length > 0) base.actionRanges = actionRanges;
  if (Object.keys(techAttackActions).length > 0) base.techAttackActions = techAttackActions;
  // The frozen affordance table (`decisions/0176`) — each record carrying its own `tg_recharge`
  // target, rate, Limited pool and display name inline.
  const affordances = buildAffordances({ sources: grantedSources, caps, limited: limitedPools, recharge, names: featureNames });
  if (affordances.length > 0) base.affordances = affordances;

  // Biological spawn-attach: `role` is the verified tech-immunity signal (case-insensitive —
  // vendor data isn't guaranteed casing), NOT `maxHeat`'s absence (drones share that and are not
  // tech-immune).
  // Attaching the status here (rather than the seed overlay matching on `role` directly)
  // keeps the overlay fold generic — it only ever reads statuses/traits a combatant carries.
  const isBiological = npcClass.role?.toLowerCase() === "biological";
  const statuses = isBiological ? [{ name: BIOLOGICAL_STATUS, expiry: { kind: "until_removed" } as const }] : [];

  // Cash the SAME `role` classifier out into the frozen destruction-outcome default — a
  // Biological NPC's destroy converts to a corpse (no wreck), everything else to an ordinary
  // wreck. `wreckConversion.ts`'s `destroyedConversionKind` reads this frozen datum, never the
  // spawn-attached `BIOLOGICAL_STATUS`; that status keeps its OTHER jobs (tech-immunity carrier,
  // display, `has_status` content targeting) but is not a destruction dispatch key.
  base.destructionOutcome = isBiological ? "corpse" : "wreck";

  // The NPC tag a template forces (COMP/CON's NPC tag: Vehicle, Ship), last applied template
  // winning as in COMP/CON's `NpcTemplateController` (`npc-gaps` 2.4). No rule keys on it; it
  // names what the unit is.
  const npcTag = composition.templates.map((t) => t.forceTag).filter((t): t is string => typeof t === "string" && t !== "").at(-1);
  if (npcTag !== undefined) base.npcTag = npcTag;

  // An NPC's CORE vocabulary is `NPC_ACTIONS` (the genuinely narrower, differently-worded RAW
  // NPC catalog — no Overcharge/Brace), stamped as `npc:*` composites.
  base.coreActionIds = coreActionIdsFor("npc");

  return ok({
    id: combatantId,
    kind: "npc",
    name: npcClass.name ?? npcClass.id ?? "NPC",
    base,
    // `stress` seeds pristine (== `maxStress`) alongside `structure` — always a real number,
    // since `maxStress` freezes from the class's stat (fallback 1). `heat` seeds 0 ONLY when
    // `base.maxHeat` is present (a real heat cap); otherwise it's omitted entirely, mirroring
    // the base field (presence-based).
    // A keyed NPC seeds each carried track from its wear, clamped to the max.
    current: {
      hp: carried(persistent?.wear?.hp, base.maxHp),
      ...(base.maxHeat !== undefined ? { heat: 0 } : {}),
      structure: carried(persistent?.wear?.structure, base.maxStructure ?? 0),
      stress: carried(persistent?.wear?.stress, base.maxStress ?? 0),
    },
    statuses,
    sourceRef: npcClass.id,
    // Tier is captured explicitly at spawn (identity, not engine): the NPC control surface
    // reads this for its tier badge. Back-deriving it from `saveTarget - 10` was never
    // sound and is now impossible — `saveTarget` is the class's printed per-tier stat
    // (8–18 across the shipped roster), not a tier-derived formula.
    tier,
    // Top-level identity, sibling of `sourceRef`/`tier` — only the template ids that actually
    // resolved (honest degrade). Omitted entirely when empty (no templates applied), matching
    // every other optional-array freeze in this file.
    ...(composition.resolvedTemplateIds.length > 0 ? { templateIds: composition.resolvedTemplateIds } : {}),
    pos: null, // unplaced until a `move_token` deploys it onto the map.
    // GM-owned by default (`null` ⇒ GM-only; the GM can act on anything). A GM may deploy an NPC
    // born owned by a player slot (Phase E) — see `SpawnNpcArgs.controllerSlotId`.
    controllerSlotId,
    ...(persistent ? { persistentNpcKey: persistent.key } : {}),
  });
}

/** A carried track value clamped into `[0, max]`, or `max` when nothing is carried. */
function carried(value: number | undefined, max: number): number {
  return value === undefined ? max : Math.max(0, Math.min(value, max));
}
